import { computed, inject, Injectable, linkedSignal, signal } from '@angular/core';
import { AuthStore } from '../../core/auth/auth-store';
import { debugLog } from '../../helpers/debugLog';
import type { MetadataResponse } from '../../models/metadata-response';
import { NubereaderExtensionService } from '../extension/nubereader-extension';
import { MetadataService } from '../metadata/metadata';
import { ReaderApi } from '../reader-api/reader-api';
import { BookContentCache } from './book-content-cache';
import { type OfflineBookRecord, OfflineBookRegistry } from './offline-db';
import { bookFilesPrefix, loanEndOf, offlineBookmarkUrl } from './offline-keys';
import { OfflineSessionService } from './offline-session';
import { snapshotOf } from './offline-writes';

export type OfflineDownloadState =
  | 'checking'
  | 'unsupported'
  | 'idle'
  | 'downloading'
  | 'downloaded'
  | 'deleting'
  | 'error'
  | 'noSpace';

class NoSpaceError extends Error {}

/** Downloads the open book for offline reading and removes it. */
@Injectable({
  providedIn: 'root',
})
export class OfflineDownloadService {
  private static readonly CONCURRENCY = 6;
  /** `metadata.size` is the zipped EPUB: leave room for the uncompressed content. */
  private static readonly SPACE_MARGIN = 2;

  private readonly metadataService = inject(MetadataService);
  private readonly authStore = inject(AuthStore);
  private readonly readerApi = inject(ReaderApi);
  private readonly registry = inject(OfflineBookRegistry);
  private readonly contentCache = inject(BookContentCache);
  private readonly extension = inject(NubereaderExtensionService);
  private readonly offlineSession = inject(OfflineSessionService);

  private readonly currentState = signal<OfflineDownloadState>('checking');
  private readonly done = signal(0);
  private readonly total = signal(0);
  private readonly downloadedRecord = signal<OfflineBookRecord | undefined>(undefined);

  /** Server flag: the book is downloaded in some browser. Kept in step with this browser's changes. */
  private readonly downloadedSomewhere = linkedSignal(
    () => this.metadataService.metadata()?.userData.downloaded ?? false,
  );

  readonly isDialogOpen = signal(false);
  /** Downloaded in another browser: syncing both copies may lose changes (NR1 warning). */
  readonly isDownloadedElsewhere = computed(
    () => this.currentState() === 'idle' && this.downloadedSomewhere(),
  );
  readonly state = this.currentState.asReadonly();
  readonly downloadedBook = this.downloadedRecord.asReadonly();
  readonly progress = computed(() => {
    const total = this.total();
    return total ? Math.round((this.done() / total) * 100) : 0;
  });

  isSupported(): boolean {
    return (
      'serviceWorker' in navigator &&
      typeof indexedDB !== 'undefined' &&
      this.contentCache.isSupported()
    );
  }

  async download(): Promise<void> {
    const metadata = this.metadataService.getRawMetadata();
    const loanKey = this.metadataService.loanKey();
    if (!metadata || !loanKey || this.currentState() === 'downloading') return;

    this.currentState.set('downloading');
    const urls = this.contentUrls(metadata);
    this.done.set(0);
    this.total.set(urls.length);
    const downloadedAt = Date.now();
    const record: OfflineBookRecord = {
      loanKey,
      status: 'downloading',
      title: metadata.metadata.title,
      downloadedAt,
      loanEnd: loanEndOf(metadata, downloadedAt),
      lastSeenAt: downloadedAt,
      metadata,
      bookmarkToken: this.metadataService.openToken(),
    };

    try {
      await this.ensureSpace(metadata);
      await this.registry.put(record);
      await this.storeAll(loanKey, urls);
      const selections = await this.readerApi.getSelectionsResource(this.authStore.token());
      // Current reader data: activities answered in this session are already in it.
      const current = this.metadataService.metadata() ?? metadata;
      await this.registry.putSnapshot(snapshotOf(loanKey, current, selections));
      record.status = 'complete';
      // Written last: a record is only "downloaded" once everything above is stored.
      await this.registry.put(record, [record.bookmarkToken, metadata.token]);
      this.downloadedRecord.set(record);
      this.currentState.set('downloaded');
      this.downloadedSomewhere.set(true);
      this.offlineSession.setDownloadedLoan(loanKey);
      void this.readerApi.setDownloaded(true).catch((error: unknown) => {
        debugLog('Could not flag the book as downloaded', error);
      });
      await this.addToExtension(metadata, record);
    } catch (error) {
      debugLog('Offline download failed', error);
      await this.discard(loanKey);
      this.currentState.set(error instanceof NoSpaceError ? 'noSpace' : 'error');
    }
  }

  async remove(): Promise<void> {
    const record = this.downloadedRecord();
    const metadata = this.metadataService.metadata();
    if (!record || !metadata) return;
    this.currentState.set('deleting');
    await this.discard(record.loanKey);
    this.downloadedRecord.set(undefined);
    this.currentState.set('idle');
    this.offlineSession.setDownloadedLoan(undefined);
    void this.extension.removeBook(metadata.otkId, record.bookmarkToken);
    if (!this.offlineSession.isOffline()) {
      this.downloadedSomewhere.set(false);
      void this.readerApi.setDownloaded(false).catch((error: unknown) => {
        debugLog('Could not flag the book as not downloaded', error);
      });
    }
  }

  /** Reads whether the open book is already downloaded in this browser. */
  async refresh(): Promise<void> {
    const loanKey = this.metadataService.loanKey();
    if (!loanKey || this.currentState() === 'downloading' || this.currentState() === 'deleting') {
      return;
    }
    if (!this.isSupported()) {
      this.currentState.set('unsupported');
      return;
    }
    try {
      const record = await this.registry.get(loanKey);
      // Leftover of a download interrupted by a closed tab or a crash.
      if (record?.status === 'downloading' && this.currentState() !== 'downloading') {
        await this.discard(loanKey);
      }
      const downloaded = record?.status === 'complete' ? record : undefined;
      this.downloadedRecord.set(downloaded);
      this.currentState.set(downloaded ? 'downloaded' : 'idle');
    } catch (error) {
      debugLog('Offline storage unavailable', error);
      this.currentState.set('unsupported');
    }
  }

  /** Chapters (fetched by Readium, AES-encrypted) and every other file of the book. */
  private contentUrls(metadata: MetadataResponse): string[] {
    const chaptersBase = new URL(metadata.readium.rootUrl, location.origin).href;
    const filesBase = new URL(bookFilesPrefix(metadata), location.origin).href;
    // Resolved like Readium's fetcher does, so the offline lookup finds them by the same URL.
    const chapters =
      this.metadataService
        .metadata()
        ?.readium2.manifest.readingOrder.items.map((link) => link.toURL(chaptersBase))
        .filter((url): url is string => url !== undefined) ?? [];
    const files = metadata.files.map((file) => new URL(file, filesBase).href);
    return [...new Set([...chapters, ...files])];
  }

  private async storeAll(loanKey: string, urls: string[]): Promise<void> {
    const queue = [...urls];
    const worker = async (): Promise<void> => {
      for (let url = queue.shift(); url !== undefined; url = queue.shift()) {
        await this.store(loanKey, url);
        this.done.update((done) => done + 1);
      }
    };
    await Promise.all(Array.from({ length: OfflineDownloadService.CONCURRENCY }, worker));
  }

  private async store(loanKey: string, url: string): Promise<void> {
    const response = await fetch(url, {
      headers: { 'token': this.authStore.token(), 'x-bookid': this.metadataService.xBookId() },
    });
    if (!response.ok) throw new Error(`Could not download ${url} (${response.status})`);
    await this.contentCache.put(loanKey, url, response);
  }

  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  private async ensureSpace(metadata: MetadataResponse): Promise<void> {
    const { storage } = navigator as Partial<Navigator>;
    if (!storage) return;
    const { quota, usage } = await storage.estimate();
    if (quota !== undefined && usage !== undefined) {
      const needed = (metadata.size || 0) * OfflineDownloadService.SPACE_MARGIN;
      if (quota - usage < needed) throw new NoSpaceError();
    }
    // Best effort: without it the browser may evict the book under storage pressure.
    await storage.persist().catch(() => false);
  }

  private async addToExtension(
    metadata: MetadataResponse,
    record: OfflineBookRecord,
  ): Promise<void> {
    const coverUrl = await this.readerApi.getCoverUrl(metadata.userData._id).catch(() => '');
    await this.extension.addBook(metadata.otkId, {
      url: offlineBookmarkUrl(location, record.bookmarkToken, this.authStore.cipherKey()),
      title: record.title,
      coverUrl,
      expired: Number.isFinite(record.loanEnd) ? record.loanEnd : 0,
    });
  }

  private async discard(loanKey: string): Promise<void> {
    await Promise.allSettled([this.contentCache.delete(loanKey), this.registry.delete(loanKey)]);
  }
}
